import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookOpen, RefreshCw, Loader2, TrendingUp, Clock, Check, Pencil } from "lucide-react";
import { fetchPricebook } from "@/lib/api";
import { PriceBookEditModal } from "@/components/PriceBookEditModal";

const fmt = (sym, n) => `${sym}${(n ?? 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  try {
    const [y, m, d] = s.split("-");
    const dt = new Date(Date.UTC(+y, +m - 1, +d));
    return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

const StatusBadge = ({ entry }) => {
  if (entry.status === "scheduled") {
    return (
      <span
        data-testid={`pricebook-badge-scheduled-${entry.name}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-dotr-ink bg-dotr-yellow text-dotr-ink font-mono text-[10px] uppercase tracking-widest"
      >
        <Clock size={10} /> Scheduled
      </span>
    );
  }
  if (entry.status === "recently_changed") {
    return (
      <span
        data-testid={`pricebook-badge-changed-${entry.name}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-dotr-ink bg-dotr-primary text-white font-mono text-[10px] uppercase tracking-widest"
      >
        <TrendingUp size={10} /> Changed
      </span>
    );
  }
  return (
    <span
      data-testid={`pricebook-badge-steady-${entry.name}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-dotr-ink bg-white text-dotr-inkmuted font-mono text-[10px] uppercase tracking-widest"
    >
      <Check size={10} /> Steady
    </span>
  );
};

const StatTile = ({ label, value, testId, accent }) => (
  <div
    className="brutal-card p-4 flex flex-col gap-1"
    data-testid={testId}
    style={accent ? { background: accent.bg, color: accent.fg } : undefined}
  >
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">{label}</span>
    <span className="font-heading font-semibold text-2xl tracking-tight">{value}</span>
  </div>
);

const FilterChip = ({ id, label, count, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(id)}
    data-testid={`pricebook-filter-${id}`}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-dotr-ink font-mono text-xs uppercase tracking-widest transition-all ${
      active
        ? "bg-dotr-ink text-white shadow-brutalSm translate-x-[1px] translate-y-[1px]"
        : "bg-white text-dotr-ink hover:shadow-brutalSm hover:translate-x-[-1px] hover:translate-y-[-1px]"
    }`}
  >
    {label}
    <span className={`px-1.5 py-px border ${active ? "border-white/60" : "border-dotr-ink/60"}`}>
      {count}
    </span>
  </button>
);

export const PriceBook = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | scheduled | recently_changed | steady
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPricebook();
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load price book.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sym = data?.currency_symbol || "£";
  const entries = data?.entries || [];

  const stats = useMemo(() => {
    const s = { total: entries.length, steady: 0, scheduled: 0, recently_changed: 0 };
    entries.forEach((e) => {
      if (s[e.status] !== undefined) s[e.status] += 1;
    });
    return s;
  }, [entries]);

  const visible = useMemo(() => {
    if (filter === "all") return entries;
    return entries.filter((e) => e.status === filter);
  }, [entries, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    visible.forEach((e) => {
      if (!map.has(e.category)) map.set(e.category, []);
      map.get(e.category).push(e);
    });
    return Array.from(map.entries());
  }, [visible]);

  return (
    <div data-testid="page-pricebook" className="space-y-6">
      <div className="brutal-card p-5 md:p-6">
        <div className="flex items-start md:items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 border-2 border-dotr-ink bg-dotr-ink text-white flex items-center justify-center">
              <BookOpen size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight">
                Price Book
              </h2>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
                Read-only · Reflects the currently priced menu · Historical orders retain their own snapshot prices
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="pricebook-refresh"
            className="brutal-btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <StatTile label="Items on menu" value={stats.total} testId="pricebook-stat-total" />
          <StatTile
            label="Steady"
            value={stats.steady}
            testId="pricebook-stat-steady"
            accent={{ bg: "#FAF7F2", fg: "#1A1714" }}
          />
          <StatTile
            label="Recently changed"
            value={stats.recently_changed}
            testId="pricebook-stat-changed"
            accent={{ bg: "#DF513B", fg: "#FFFFFF" }}
          />
          <StatTile
            label="Scheduled"
            value={stats.scheduled}
            testId="pricebook-stat-scheduled"
            accent={{ bg: "#E8B241", fg: "#1A1714" }}
          />
        </div>

        <div className="flex flex-wrap gap-2 mt-5" data-testid="pricebook-filters">
          <FilterChip id="all" label="All" count={stats.total} active={filter === "all"} onClick={setFilter} />
          <FilterChip id="scheduled" label="Scheduled" count={stats.scheduled} active={filter === "scheduled"} onClick={setFilter} />
          <FilterChip id="recently_changed" label="Recently changed" count={stats.recently_changed} active={filter === "recently_changed"} onClick={setFilter} />
          <FilterChip id="steady" label="Steady" count={stats.steady} active={filter === "steady"} onClick={setFilter} />
        </div>
      </div>

      {loading && !data && (
        <div className="brutal-card p-10 flex items-center justify-center" data-testid="pricebook-loading">
          <Loader2 size={22} className="animate-spin" />
        </div>
      )}

      {!loading && grouped.length === 0 && (
        <div className="brutal-card p-10 text-center" data-testid="pricebook-empty">
          <div className="font-mono text-xs uppercase tracking-[0.18em] text-dotr-inkmuted">
            No items match this filter.
          </div>
        </div>
      )}

      {grouped.map(([category, items]) => (
        <div key={category} className="brutal-card overflow-hidden" data-testid={`pricebook-cat-${category}`}>
          <div className="border-b-2 border-dotr-ink p-4 md:p-5 bg-dotr-ink text-white flex items-center justify-between">
            <div className="font-heading font-semibold text-lg uppercase tracking-wider">{category}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">
              {items.length} item{items.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="divide-y-2 divide-dotr-ink">
            {items.map((e) => (
              <div
                key={`${category}-${e.name}`}
                data-testid={`pricebook-row-${e.name}`}
                className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto_auto] gap-3 md:gap-6 items-center"
              >
                <div className="font-body text-base flex items-center gap-2">
                  <span>{e.name}</span>
                  {e.overridden && (
                    <span
                      className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-dotr-ink/40 text-dotr-inkmuted"
                      title="This price has an admin override — click Edit to change or revert."
                      data-testid={`pricebook-override-tag-${e.name}`}
                    >
                      overridden
                    </span>
                  )}
                </div>

                <div className="flex flex-col leading-tight">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">Current</span>
                  <span
                    className="font-heading font-semibold text-2xl text-dotr-ink tracking-tight"
                    data-testid={`pricebook-current-${e.name}`}
                  >
                    {fmt(sym, e.current_price)}
                  </span>
                </div>

                <div className="flex flex-col leading-tight">
                  {e.status === "scheduled" && (
                    <>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                        Upcoming from {fmtDate(e.effective_from)}
                      </span>
                      <span
                        className="font-heading font-semibold text-xl text-dotr-primary tracking-tight"
                        data-testid={`pricebook-upcoming-${e.name}`}
                      >
                        → {fmt(sym, e.upcoming_price)}
                      </span>
                    </>
                  )}
                  {e.status === "recently_changed" && (
                    <>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                        Was {fmt(sym, e.previous_price)} until {fmtDate(e.changed_on)}
                      </span>
                      <span
                        className="font-mono text-xs line-through text-dotr-inkmuted"
                        data-testid={`pricebook-previous-${e.name}`}
                      >
                        {fmt(sym, e.previous_price)}
                      </span>
                    </>
                  )}
                  {e.status === "steady" && (
                    <span className="font-mono text-[11px] uppercase tracking-widest text-dotr-inkmuted">
                      No scheduled change
                    </span>
                  )}
                </div>

                <div className="justify-self-start md:justify-self-end">
                  <StatusBadge entry={e} />
                </div>

                <div className="justify-self-start md:justify-self-end">
                  <button
                    type="button"
                    onClick={() => setEditing(e)}
                    data-testid={`pricebook-edit-${e.name}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-dotr-ink bg-white hover:bg-dotr-yellow hover:text-dotr-ink transition-colors font-heading uppercase tracking-wider text-xs"
                    aria-label={`Edit price for ${e.name}`}
                  >
                    <Pencil size={12} /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {data?.as_of && (
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted text-right" data-testid="pricebook-asof">
          As of {new Date(data.as_of).toLocaleString()}
        </div>
      )}

      {editing && (
        <PriceBookEditModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default PriceBook;
